import React from 'react';
import './terms-conditions.css';

export default function TermsConditionsData() {
    const lastUpdated = "August 4, 2026";

    const sections = [
        {
            id: "acceptance",
            title: "Acceptance of Terms",
            content: (
                <>
                    <p>
                        Welcome to <strong>Spinnennetz</strong>. By accessing or using our website at <a href="https://www.thespinnennetz.com">www.thespinnennetz.com</a> or any of our services, you confirm that you have read, understood, and agree to be bound by these Terms and Conditions ("Terms"), our Privacy Policy, and any additional guidelines or rules applicable to specific features of our services.
                    </p>
                    <p>
                        These Terms constitute a legally binding agreement between you ("User," "Client," or "you") and Spinnennetz ("Company," "we," "our," or "us"). If you do not agree to these Terms, you must immediately discontinue use of our website and services.
                    </p>
                    <p>
                        We reserve the right to modify these Terms at any time. Continued use of our website after any changes constitutes your acceptance of the new Terms.
                    </p>
                </>
            ),
        },
        {
            id: "services",
            title: "Our Services",
            content: (
                <>
                    <p>Spinnennetz provides a range of digital services, including but not limited to:</p>
                    <ul className="tc-list tc-check-list">
                        <li>Conversion Rate Optimization (CRO) — including CRO sprints, monthly CRO retainers, and A/B testing setup</li>
                        <li>Conversion-focused Landing Page Design and Development</li>
                        <li>eCommerce Store Development (Shopify, WooCommerce)</li>
                        <li>Website Design and Development (Basic Web, CMS Web)</li>
                        <li>Web Maintenance and Support</li>
                        <li>CRO Tool Setup and Analytics Integration</li>
                        <li>AI-assisted Testing and Data-driven Design Consulting</li>
                    </ul>
                    <p>
                        The scope, timeline, deliverables, and pricing of all services are defined in separate written agreements, proposals, or statements of work ("SOW") agreed upon between Spinnennetz and the client prior to commencement of work.
                    </p>
                </>
            ),
        },
        {
            id: "user-obligations",
            title: "User Obligations",
            content: (
                <>
                    <p>By using our website and services, you agree to:</p>
                    <ul className="tc-list tc-check-list">
                        <li>Provide accurate, current, and complete information when submitting forms or engaging with our services</li>
                        <li>Not use our website for any unlawful, harmful, or fraudulent purpose</li>
                        <li>Not attempt to gain unauthorized access to any part of our website, servers, or connected systems</li>
                        <li>Not transmit any viruses, malware, or other harmful code</li>
                        <li>Not scrape, crawl, or copy content from our website without prior written consent</li>
                        <li>Comply with all applicable local, national, and international laws and regulations</li>
                        <li>Respect the intellectual property rights of Spinnennetz and third parties</li>
                    </ul>
                    <p>
                        We reserve the right to suspend or terminate access to any user who violates these obligations without prior notice.
                    </p>
                </>
            ),
        },
        {
            id: "intellectual-property",
            title: "Intellectual Property",
            content: (
                <>
                    <div className="tc-subsection">
                        <h4>Spinnennetz Content</h4>
                        <p>
                            All content on our website — including but not limited to text, graphics, logos, icons, images, code, design elements, and methodologies — is the exclusive property of Spinnennetz or its licensed content providers and is protected under applicable intellectual property laws. Unauthorized reproduction, distribution, or modification of any content is strictly prohibited.
                        </p>
                    </div>
                    <div className="tc-subsection">
                        <h4>Client Content & Deliverables</h4>
                        <p>
                            Upon receipt of full payment for the agreed project, intellectual property rights for custom deliverables (such as designed web pages, written copy, or custom code) will transfer to the client, as outlined in the respective project agreement. Pre-existing tools, frameworks, templates, or proprietary methodologies developed by Spinnennetz remain the intellectual property of Spinnennetz.
                        </p>
                    </div>
                    <div className="tc-subsection">
                        <h4>Feedback & Suggestions</h4>
                        <p>
                            Any feedback, ideas, or suggestions you submit to us may be used freely by Spinnennetz for any purpose without compensation or attribution to you.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "payment-terms",
            title: "Payment & Billing",
            content: (
                <>
                    <p>All payment terms are defined in your project proposal or service agreement. General terms include:</p>
                    <div className="tc-subsection">
                        <h4>Invoicing & Fees</h4>
                        <ul className="tc-list">
                            <li>Fees are quoted in the project proposal and are due as per the milestones or schedule defined therein</li>
                            <li>A deposit (typically 50% of the total project cost) is required before work commences unless otherwise agreed in writing</li>
                            <li>All fees are exclusive of applicable taxes unless explicitly stated otherwise</li>
                        </ul>
                    </div>
                    <div className="tc-subsection">
                        <h4>Late Payments</h4>
                        <p>
                            Invoices unpaid beyond the agreed due date may incur interest at a rate of 1.5% per month or the maximum rate permitted by law, whichever is lower. We reserve the right to pause or suspend ongoing work until outstanding payments are settled.
                        </p>
                    </div>
                    <div className="tc-subsection">
                        <h4>Refund Policy</h4>
                        <p>
                            Due to the nature of digital and creative services, deposits and payments made for work already completed are non-refundable. Disputes regarding deliverable quality must be raised in writing within 7 days of delivery. Refunds, if applicable, will be considered on a case-by-case basis at Spinnennetz's sole discretion.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "confidentiality",
            title: "Confidentiality",
            content: (
                <>
                    <p>
                        Both Spinnennetz and the client may share confidential information in the course of working together, including business data, marketing strategies, analytics, technical information, and proprietary processes ("Confidential Information").
                    </p>
                    <p>
                        Each party agrees to:
                    </p>
                    <ul className="tc-list tc-check-list">
                        <li>Keep all Confidential Information strictly private and not disclose it to third parties without prior written consent</li>
                        <li>Use Confidential Information solely for the purpose of fulfilling the agreed project or services</li>
                        <li>Apply reasonable security measures to protect Confidential Information from unauthorized access or disclosure</li>
                    </ul>
                    <p>
                        This obligation of confidentiality shall survive the termination of the business relationship for a period of two (2) years.
                    </p>
                </>
            ),
        },
        {
            id: "disclaimers",
            title: "Disclaimers & No Guarantees",
            content: (
                <>
                    <p>
                        Our website and services are provided on an <strong>"as is"</strong> and <strong>"as available"</strong> basis. Spinnennetz makes no warranties, express or implied, including but not limited to:
                    </p>
                    <ul className="tc-list">
                        <li>Warranties of merchantability or fitness for a particular purpose</li>
                        <li>Guarantees that our website will be uninterrupted, error-free, or free from viruses</li>
                        <li>Guarantees of specific business outcomes or conversion rate improvements from CRO services</li>
                        <li>Accuracy or completeness of information provided on the website</li>
                    </ul>
                    <p>
                        While we strive to deliver exceptional results through data-driven strategies, digital marketing and CRO outcomes depend on many external variables (e.g., market conditions, platform changes, audience behavior) outside our control. We cannot guarantee specific revenue increases, traffic growth, or conversion rate targets.
                    </p>
                </>
            ),
        },
        {
            id: "limitation-liability",
            title: "Limitation of Liability",
            content: (
                <>
                    <p>
                        To the fullest extent permitted by applicable law, Spinnennetz shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
                    </p>
                    <ul className="tc-list">
                        <li>Loss of profits, revenue, or business opportunities</li>
                        <li>Loss of data or information</li>
                        <li>Business interruption or downtime</li>
                        <li>Any damages arising from reliance on content or services provided by us</li>
                    </ul>
                    <p>
                        In any event, Spinnennetz's maximum aggregate liability to any client shall not exceed the total fees paid by that client for the specific service giving rise to the claim in the three (3) months preceding the event.
                    </p>
                </>
            ),
        },
        {
            id: "third-party",
            title: "Third-Party Tools & Links",
            content: (
                <>
                    <p>
                        Our services may involve the use of or integration with third-party tools, platforms, and software (e.g., Google Optimize, VWO, Hotjar, Shopify, WooCommerce, Google Analytics). Your use of such third-party tools is governed by their respective terms of service and privacy policies. Spinnennetz is not responsible for the availability, accuracy, or reliability of third-party services.
                    </p>
                    <p>
                        Our website may also contain links to external websites for informational purposes. These links do not constitute an endorsement of any third-party site, and Spinnennetz accepts no responsibility for their content or privacy practices.
                    </p>
                </>
            ),
        },
        {
            id: "termination",
            title: "Termination",
            content: (
                <>
                    <div className="tc-subsection">
                        <h4>Termination by Client</h4>
                        <p>
                            Clients may terminate a service engagement by providing written notice as specified in the project agreement. Any work completed up to the termination date will be invoiced and payable in full. Deposits are non-refundable.
                        </p>
                    </div>
                    <div className="tc-subsection">
                        <h4>Termination by Spinnennetz</h4>
                        <p>
                            We reserve the right to terminate any engagement or suspend website access with or without cause. Grounds for termination include, but are not limited to, non-payment, breach of these Terms, or conduct we determine to be harmful to our business or reputation.
                        </p>
                    </div>
                    <div className="tc-subsection">
                        <h4>Effect of Termination</h4>
                        <p>
                            Upon termination, all rights and licenses granted to you under any project agreement will immediately cease. Provisions relating to confidentiality, intellectual property, limitation of liability, and governing law shall survive termination.
                        </p>
                    </div>
                </>
            ),
        },
        {
            id: "governing-law",
            title: "Governing Law & Disputes",
            content: (
                <>
                    <p>
                        These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions. Any disputes arising out of or relating to these Terms or our services shall first be attempted to be resolved through good-faith negotiation between the parties.
                    </p>
                    <p>
                        If a resolution cannot be reached amicably within 30 days, any disputes shall be subject to the exclusive jurisdiction of the courts located in <strong>Surat, Gujarat, India</strong>.
                    </p>
                </>
            ),
        },
        {
            id: "changes",
            title: "Changes to These Terms",
            content: (
                <>
                    <p>
                        We reserve the right to update or revise these Terms and Conditions at any time. Any changes will be posted on this page with an updated "Last Updated" date. It is your responsibility to review these Terms periodically.
                    </p>
                    <p>
                        Your continued use of our website or services after any changes constitutes your acceptance of the revised Terms. If you disagree with any updated Terms, you must stop using our services immediately.
                    </p>
                </>
            ),
        },
        {
            id: "contact",
            title: "Contact Us",
            content: (
                <>
                    <p>
                        For any questions, concerns, or clarifications regarding these Terms and Conditions, please contact us:
                    </p>
                    <div className="tc-contact-info">
                        <div className="tc-contact-item">
                            <span className="tc-contact-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M9.366 10.682C10.3043 12.3305 11.6695 13.6957 13.318 14.634L14.202 13.396C14.3442 13.1969 14.5543 13.0569 14.7928 13.0023C15.0313 12.9478 15.2814 12.9825 15.496 13.1C16.9103 13.8729 18.4722 14.3378 20.079 14.464C20.3298 14.4839 20.5638 14.5975 20.7345 14.7823C20.9052 14.9671 21 15.2094 21 15.461V19.923C21.0001 20.1706 20.9083 20.4094 20.7424 20.5932C20.5765 20.777 20.3483 20.8927 20.102 20.918C19.572 20.973 19.038 21 18.5 21C9.94 21 3 14.06 3 5.5C3 4.962 3.027 4.428 3.082 3.898C3.10725 3.6517 3.22298 3.42352 3.40679 3.25763C3.5906 3.09175 3.82941 2.99995 4.077 3H8.539C8.79056 2.99997 9.0329 3.09475 9.21768 3.26545C9.40247 3.43615 9.51613 3.67022 9.536 3.921C9.66222 5.52779 10.1271 7.08968 10.9 8.504C11.0175 8.71856 11.0522 8.96874 10.9977 9.2072C10.9431 9.44565 10.8031 9.65584 10.604 9.798L9.366 10.682ZM6.844 10.025L8.744 8.668C8.20478 7.50409 7.83535 6.26884 7.647 5H5.01C5.004 5.166 5.001 5.333 5.001 5.5C5 12.956 11.044 19 18.5 19C18.667 19 18.834 18.997 19 18.99V16.353C17.7312 16.1646 16.4959 15.7952 15.332 15.256L13.975 17.156C13.4287 16.9437 12.898 16.6931 12.387 16.406L12.329 16.373C10.3676 15.2567 8.74328 13.6324 7.627 11.671L7.594 11.613C7.30691 11.102 7.05628 10.5713 6.844 10.025Z" fill="#57007B" />
                                </svg>
                            </span>
                            <div>
                                <span className="tc-contact-label">Phone</span>
                                <a href="tel:+919624029330">+91 9624029330</a>
                            </div>
                        </div>
                        <div className="tc-contact-item">
                            <span className="tc-contact-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M3 3H21C21.2652 3 21.5196 3.10536 21.7071 3.29289C21.8946 3.48043 22 3.73478 22 4V20C22 20.2652 21.8946 20.5196 21.7071 20.7071C21.5196 20.8946 21.2652 21 21 21H3C2.73478 21 2.48043 20.8946 2.29289 20.7071C2.10536 20.5196 2 20.2652 2 20V4C2 3.73478 2.10536 3.48043 2.29289 3.29289C2.48043 3.10536 2.73478 3 3 3ZM20 7.238L12.072 14.338L4 7.216V19H20V7.238ZM4.511 5L12.061 11.662L19.502 5H4.511Z" fill="#57007B" />
                                </svg>
                            </span>
                            <div>
                                <span className="tc-contact-label">Email</span>
                                <a href="mailto:info@thespinnennetz.com">info@thespinnennetz.com</a>
                            </div>
                        </div>
                        <div className="tc-contact-item">
                            <span className="tc-contact-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM9.71 19.667C8.72341 17.5743 8.15187 15.3102 8.027 13H4.062C4.25659 14.5389 4.89392 15.9882 5.89657 17.1717C6.89922 18.3552 8.22401 19.2221 9.71 19.667ZM10.03 13C10.181 15.439 10.878 17.73 12 19.752C13.1523 17.6766 13.8254 15.3695 13.97 13H10.03ZM19.938 13H15.973C15.8481 15.3102 15.2766 17.5743 14.29 19.667C15.776 19.2221 17.1008 18.3552 18.1034 17.1717C19.1061 15.9882 19.7434 14.5389 19.938 13ZM4.062 11H8.027C8.15187 8.68979 8.72341 6.42569 9.71 4.333C8.22401 4.77788 6.89922 5.64475 5.89657 6.8283C4.89392 8.01184 4.25659 9.4611 4.062 11ZM10.031 11H13.969C13.8248 8.6306 13.152 6.32353 12 4.248C10.8477 6.32345 10.1746 8.63052 10.03 11H10.031ZM14.29 4.333C15.2766 6.42569 15.8481 8.68979 15.973 11H19.938C19.7434 9.4611 19.1061 8.01184 18.1034 6.8283C17.1008 5.64475 15.776 4.77788 14.29 4.333Z" fill="#57007B" />
                                </svg>
                            </span>
                            <div>
                                <span className="tc-contact-label">Website</span>
                                <a href="https://www.thespinnennetz.com" target="_blank" rel="noreferrer">www.thespinnennetz.com</a>
                            </div>
                        </div>
                    </div>
                </>
            ),
        },
    ];

    return (
        <section className="tc-page cSection">
            <div className="cContainer">
                {/* Hero */}
                <div className="tc-hero">
                    <div className="tc-hero-badge">Legal</div>
                    <h1 className="tc-hero-title">Terms &amp; Conditions</h1>
                    <p className="tc-hero-subtitle">
                        Please read these terms carefully before using Spinnennetz's website or services.
                    </p>
                    <div className="tc-last-updated">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM13 12V7H11V14H17V12H13Z" fill="currentColor" />
                        </svg>
                        Last updated: {lastUpdated}
                    </div>
                </div>

                {/* Layout */}
                <div className="tc-layout">
                    {/* Sidebar TOC */}
                    <aside className="tc-sidebar">
                        <div className="tc-toc">
                            <h3 className="tc-toc-title">Table of Contents</h3>
                            <ul className="tc-toc-list">
                                {sections.map((section, idx) => (
                                    <li key={section.id}>
                                        <a href={`#${section.id}`} className="tc-toc-link">
                                            <span className="tc-toc-num">{String(idx + 1).padStart(2, '0')}</span>
                                            {section.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>

                    {/* Content */}
                    <main className="tc-content">
                        {sections.map((section, idx) => (
                            <div className="tc-section" id={section.id} key={section.id}>
                                <div className="tc-section-header">
                                    <span className="tc-section-num">{String(idx + 1).padStart(2, '0')}</span>
                                    <h2 className="tc-section-title">{section.title}</h2>
                                </div>
                                <div className="tc-section-body">
                                    {section.content}
                                </div>
                            </div>
                        ))}
                    </main>
                </div>
            </div>
        </section>
    );
}
